import React, { useRef, useEffect, useState, useCallback, KeyboardEvent } from 'react';
import {
    DrawerOverlay,
    DrawerPanel,
    DrawerHeader,
    DrawerTitle,
    DrawerHeaderActions,
    HeaderIconButton,
    MessageArea,
    WelcomeContainer,
    WelcomeIcon,
    WelcomeTitle,
    WelcomeSubtitle,
    SuggestionsGrid,
    SuggestionChip,
    TypingDots,
    InputArea,
    ChatInput,
    SendButton,
    StopButton,
    PreviewBadge,
    BubbleRow,
    Bubble,
} from './AiChat.styles';
import AiChatMessage from './AiChatMessage';
import { ChatMessage, ChatSuggestion } from './types';

interface AiChatDrawerProps {
    messages: ChatMessage[];
    isLoading: boolean;
    suggestions: ChatSuggestion[];
    onSend: (content: string) => void;
    onStop: () => void;
    onClear: () => void;
    onClose: () => void;
}

const AiChatDrawer: React.FC<AiChatDrawerProps> = ({ messages, isLoading, suggestions, onSend, onStop, onClear, onClose }) => {
    const [input, setInput] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const messageEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Auto-scroll when new messages arrive
    useEffect(() => {
        messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input on open
    useEffect(() => {
        const timer = setTimeout(() => inputRef.current?.focus(), 300);
        return () => clearTimeout(timer);
    }, []);

    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(onClose, 240);
    }, [onClose]);

    const handleSubmit = useCallback(
        (e?: React.FormEvent) => {
            e?.preventDefault();
            if (!input.trim() || isLoading) return;
            onSend(input);
            setInput('');
            // Reset textarea height
            if (inputRef.current) inputRef.current.style.height = 'auto';
        },
        [input, isLoading, onSend]
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    const handleSuggestionClick = useCallback(
        (prompt: string) => {
            onSend(prompt);
        },
        [onSend]
    );

    // Auto-resize textarea
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
        const el = e.target;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, []);

    const handleOverlayClick = useCallback(
        (e: React.MouseEvent) => {
            if (e.target === e.currentTarget) handleClose();
        },
        [handleClose]
    );

    const showWelcome = messages.length === 0 && !isLoading;

    return (
        <>
            <DrawerOverlay $isClosing={isClosing} onClick={handleOverlayClick} />
            <DrawerPanel $isClosing={isClosing}>
                {/* Header */}
                <DrawerHeader>
                    <DrawerTitle>
                        <span>🤖</span>
                        <span>OrcaWeb Assistant</span>
                    </DrawerTitle>
                    <DrawerHeaderActions>
                        {messages.length > 0 && (
                            <HeaderIconButton onClick={onClear} title="Clear conversation" aria-label="Clear conversation">
                                🗑️
                            </HeaderIconButton>
                        )}
                        <HeaderIconButton onClick={handleClose} title="Close" aria-label="Close chat">
                            ✕
                        </HeaderIconButton>
                    </DrawerHeaderActions>
                </DrawerHeader>

                {/* Messages */}
                <MessageArea>
                    {showWelcome && (
                        <WelcomeContainer>
                            <WelcomeIcon>🐳</WelcomeIcon>
                            <WelcomeTitle>OrcaWeb AI Assistant</WelcomeTitle>
                            <WelcomeSubtitle>Ask me anything about OrcaWeb — navigation, vessel operations, reporting, and more.</WelcomeSubtitle>
                            {suggestions.length > 0 && (
                                <SuggestionsGrid>
                                    {suggestions.map((s) => (
                                        <SuggestionChip key={s.prompt} onClick={() => handleSuggestionClick(s.prompt)}>
                                            {s.label}
                                        </SuggestionChip>
                                    ))}
                                </SuggestionsGrid>
                            )}
                        </WelcomeContainer>
                    )}

                    {messages.map((msg) => (
                        <AiChatMessage key={msg.id} message={msg} />
                    ))}

                    {/* Typing indicator when loading and last message isn't streaming yet */}
                    {isLoading && messages.length > 0 && !messages[messages.length - 1]?.isStreaming && (
                        <BubbleRow $isUser={false}>
                            <Bubble $isUser={false}>
                                <TypingDots>
                                    <span />
                                    <span />
                                    <span />
                                </TypingDots>
                            </Bubble>
                        </BubbleRow>
                    )}

                    <div ref={messageEndRef} />
                </MessageArea>

                {/* Input */}
                <InputArea onSubmit={handleSubmit}>
                    <ChatInput
                        ref={inputRef}
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask something..."
                        rows={1}
                        disabled={false}
                    />
                    {isLoading ? (
                        <StopButton onClick={onStop} title="Stop generating" aria-label="Stop generating">
                            ■
                        </StopButton>
                    ) : (
                        <SendButton type="submit" $active={!!input.trim()} disabled={!input.trim()} title="Send message" aria-label="Send message">
                            ➤
                        </SendButton>
                    )}
                </InputArea>
                <PreviewBadge>Preview — mock responses · AI integration coming soon</PreviewBadge>
            </DrawerPanel>
        </>
    );
};

export default AiChatDrawer;
